/**
 * The API's account events websocket (GET /events): how one tab hears about
 * changes made elsewhere -- a platform connected in another tab, someone
 * entering your giveaway.
 *
 * Browsers can't put an Authorization header on a websocket, so the first
 * message signs in. The socket reconnects with backoff when it drops, and
 * stops for good if the server says the token is no good (the next ordinary
 * request will sign the user out).
 */

export type AccountEvent =
  { type: 'platforms_changed'; platform: string } | { type: 'notification'; notification: unknown }

/** Close code the API uses for a rejected token. */
export const CLOSE_UNAUTHORIZED = 4401

const HEARTBEAT_MS = 25_000
const MAX_BACKOFF_MS = 30_000

/** ws(s):// address of /events, from the API's base URL (which may be relative). */
export function accountEventsUrl(apiBaseUrl: string, pageUrl: string): string {
  const url = new URL(apiBaseUrl.replace(/\/+$/, '') + '/events', pageUrl)
  url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:'
  return url.toString()
}

/** Delay before reconnect attempt `attempt` (0-based): doubling, capped, jittered. */
export function backoffMs(attempt: number, random: () => number = Math.random): number {
  const base = Math.min(MAX_BACKOFF_MS, 1000 * 2 ** attempt)
  return Math.round(base * (0.75 + random() * 0.5))
}

export function parseAccountEvent(raw: unknown): AccountEvent | { type: 'ready' } | null {
  if (typeof raw !== 'string') return null
  let data: unknown
  try {
    data = JSON.parse(raw)
  } catch {
    return null
  }
  if (typeof data !== 'object' || data === null) return null
  const event = data as Record<string, unknown>
  switch (event.type) {
    case 'ready':
      return { type: 'ready' }
    case 'platforms_changed':
      return typeof event.platform === 'string'
        ? { type: 'platforms_changed', platform: event.platform }
        : null
    case 'notification':
      return { type: 'notification', notification: event.notification }
    default:
      return null
  }
}

export interface AccountEventsOptions {
  url: string
  token: string
  onEvent: (event: AccountEvent) => void
  /** Signed in and listening. `reconnected` is false the first time. */
  onReady?: (reconnected: boolean) => void
  /** For tests. */
  WebSocketImpl?: typeof WebSocket
}

/** Opens the socket and keeps it open. Returns a function that closes it. */
export function openAccountEvents({
  url,
  token,
  onEvent,
  onReady,
  WebSocketImpl = WebSocket,
}: AccountEventsOptions): () => void {
  let socket: WebSocket | null = null
  let stopped = false
  let attempt = 0
  let everReady = false
  let retryTimer: ReturnType<typeof setTimeout> | undefined
  let heartbeat: ReturnType<typeof setInterval> | undefined

  const connect = () => {
    if (stopped) return
    const ws = new WebSocketImpl(url)
    socket = ws

    ws.onopen = () => ws.send(JSON.stringify({ type: 'auth', token }))

    ws.onmessage = (message) => {
      const event = parseAccountEvent(message.data)
      if (!event) return
      if (event.type === 'ready') {
        attempt = 0
        clearInterval(heartbeat)
        heartbeat = setInterval(() => {
          if (ws.readyState === WebSocketImpl.OPEN) ws.send('{"type":"ping"}')
        }, HEARTBEAT_MS)
        onReady?.(everReady)
        everReady = true
        return
      }
      onEvent(event)
    }

    ws.onclose = (close) => {
      clearInterval(heartbeat)
      if (socket !== ws) return
      socket = null
      if (stopped || close.code === CLOSE_UNAUTHORIZED) return
      retryTimer = setTimeout(connect, backoffMs(attempt++))
    }
  }

  connect()

  return () => {
    stopped = true
    clearTimeout(retryTimer)
    clearInterval(heartbeat)
    socket?.close()
    socket = null
  }
}
